// @ts-nocheck
/**
 * types.ts — Template V2 M3 Renderer Core Types (WP-017)
 *
 * Defines the serializable RenderDescriptor output contract, typed component
 * props per kind, and the ComponentRenderer interface.
 *
 * No React Native or Tamagui imports — pure TypeScript (DEC-016, DEC-018).
 */

import type { ValidationIssue } from '../../diagnostics';
import type {
    AssetRef,
    ComponentPressHandler,
    ConfirmDialog,
    LocalizedText,
    RatingGlyphId,
    TemplateFontId,
    TextFormat,
    ValueSource,
} from '../../types/canonical';
import type { AssetResolutionContext } from '../assets/types';
import type { BindingContext } from '../binding';
import type { CompiledRuntimeNode, CompiledRuntimeTemplate } from '../compiler';
import type { ExpressionEngine } from '../expressions';

// ── Serializable output contract ──────────────────────────────────────────────

/**
 * Serializable render output produced by a ComponentRenderer.
 *
 * Contains all layout, state, and content a platform adapter (e.g. an RN
 * adapter) needs to produce visual output. Never contains React elements
 * (DEC-016). diagnostics uses ValidationIssue[] for uniformity with the
 * rest of the v2 diagnostic system.
 */
export interface RenderChrome {
  color?: string;
  backgroundColor?: string;
  fontSize?: number;
  /** Resolved from StyleBlock.fontWeight. */
  fontWeight?: 'normal' | 'bold' | 'semibold' | 'light';
  /**
   * Resolved catalog id from StyleBlock.fontFamily (DEC-064).
   * Always set by resolveStyleBlock (defaults to nunitoSans when omitted).
   */
  fontFamily?: TemplateFontId;
  /** Resolved from StyleBlock.textAlign. */
  textAlign?: 'left' | 'center' | 'right' | 'justify';
  borderColor?: string;
  borderWidth?: number;
  borderRadius?: number;
  opacity?: number;
  /** Resolved from StyleBlock.padding. */
  padding?: number | { top?: number; bottom?: number; left?: number; right?: number };
  /** Resolved from StyleBlock.fontStyle; maps V1 isItalic. */
  fontStyle?: 'normal' | 'italic';
  /** Resolved from StyleBlock.textDecoration; maps V1 isUnderlined / isStrikethrough. */
  textDecoration?: 'none' | 'underline' | 'line-through' | 'underline line-through';
  /** Resolved from StyleBlock.verticalAlign; maps V1 verticalAlign. */
  verticalAlign?: 'top' | 'center' | 'bottom';
  /** Resolved from StyleBlock.elevation; omitted or 0 = flat. */
  elevation?: 0 | 1 | 2;
  /** Resolved from StyleBlock.bevel; omitted = flat single-color border. */
  bevel?: 'raised' | 'inset';
  /** Max visible lines forwarded from TextComponent.text.maxLines; maps V1 maxLineNumber. */
  maxLines?: number;
  /** Overflow strategy forwarded from TextComponent.text.overflow. */
  overflow?: 'clip' | 'ellipsis' | 'ellipsisStart' | 'wrap';
}

export interface RenderDescriptor {
  nodeId: string;
  type: string;
  layout: RenderedLayout;
  visible: boolean;
  enabled: boolean;
  props: Record<string, unknown>;
  chrome?: RenderChrome;
  children?: RenderDescriptor[];
  diagnostics?: ValidationIssue[];
}

/**
 * Fully resolved layout geometry for a rendered node.
 * All coordinate values are normalized fractions in [0..1] relative to the item surface.
 * The platform adapter is responsible for scaling these to device pixels.
 */
export interface RenderedLayout {
  /** Horizontal offset as a fraction of the item surface width. Range [0..1]. */
  x: number;
  /** Vertical offset as a fraction of the item surface height. Range [0..1]. */
  y: number;
  /** Width as a fraction of the item surface width. 1 = 100%. */
  width: number;
  /** Height as a fraction of the item surface height. 1 = 100%. */
  height: number;
  zIndex?: number;
  /**
   * Rotation in degrees. Undefined when governed by an expression and
   * expressionEngine is null (DEC-019 fail-open: omit rotation, do not crash).
   */
  rotation?: number;
}

// ── M3 component type subset ──────────────────────────────────────────────────

/**
 * Component types with a registered renderer in M3.
 * badge, progress, and customToken are deferred post-M3.
 */
export type M3RenderableType =
  | 'text'
  | 'number'
  | 'boolean'
  | 'date'
  | 'enum'
  | 'stringSelect'
  | 'image'
  | 'svg'
  | 'html'
  | 'qrCode'
  | 'rating'
  | 'color'
  | 'button'
  | 'icon'
  | 'group'
  | 'spacer'
  | 'dragHandle'
  | 'line'
  | 'table'
  | 'chart'
  | 'slider'
  | 'timerDisplay'
  | 'listView';

// ── Typed props per component kind ────────────────────────────────────────────

/**
 * Typed props shapes for each M3 renderable component kind.
 *
 * Shapes mirror the canonical ComponentNode interfaces in types/component.ts.
 * Access via extractTypedProps<T>() at the renderer boundary rather than
 * scattering unsafe casts throughout renderer bodies (DEC-020).
 *
 * GroupComponent.group.children is intentionally omitted — renderers must
 * use CompiledRuntimeNode.childIds + ChildRenderFunction for recursion.
 */
export interface CompiledPropsByType {
  text: {
    text: {
      source: ValueSource<string>;
      /** Optional outbound href (URL / mailto / tel / item:…). */
      href?: ValueSource<string>;
      format?: TextFormat;
      editable?: boolean;
      placeholder?: LocalizedText;
      multiline?: boolean;
      /** Maps V1 maxLineNumber. */
      maxLines?: number;
      /** Overflow strategy when maxLines is exceeded.
       *  `ellipsis` = truncate end (RN tail); `ellipsisStart` = truncate start (RN head). */
      overflow?: 'clip' | 'ellipsis' | 'ellipsisStart' | 'wrap';
    };
  };
  number: {
    number: {
      source: ValueSource<number>;
      format?: TextFormat;
      /** Maximum fraction digits for display; omit = unlimited. */
      maxDigits?: number;
      editable?: boolean;
      placeholder?: LocalizedText;
    };
  };
  boolean: {
    boolean: {
      source: ValueSource<boolean>;
      trueLabel?: LocalizedText;
      falseLabel?: LocalizedText;
      variant?: 'toggle' | 'checkbox' | 'icon';
      labelPlacement?: 'trailing' | 'sides';
      trueColor?: ValueSource<string>;
      falseColor?: ValueSource<string>;
      outlineColor?: ValueSource<string>;
      editable?: boolean;
    };
  };
  date: {
    date: {
      source: ValueSource<string>;
      format?: string;
      editable?: boolean;
      pickerMode?: 'date' | 'time' | 'datetime';
    };
  };
  enum: {
    enum: {
      source: ValueSource<string>;
      categoryId: string;
      propertyId: string;
      editable?: boolean;
      displayMode?: 'label' | 'chip' | 'icon';
    };
  };
  stringSelect: {
    stringSelect: {
      source: ValueSource<string>;
      separator?: string;
      editable?: boolean;
      displayMode?: 'label' | 'chip';
    };
  };
  image: {
    image: {
      source: ValueSource<string> | AssetRef;
      /** Optional outbound href (URL / mailto / tel / item:…). */
      href?: ValueSource<string>;
      fit?: 'contain' | 'cover' | 'fill';
      editable?: boolean;
      fallback?: 'none' | 'placeholder' | 'icon';
    };
  };
  svg: {
    svg: {
      source: AssetRef;
      fit?: 'contain' | 'cover' | 'fill';
      pointerEvents?: 'none' | 'auto';
      fill?: ValueSource<string>;
      stroke?: ValueSource<string>;
      strokeWidth?: ValueSource<number>;
    };
  };
  html: {
    html: {
      /** Bare https URL, `<iframe>` snippet, or script-free HTML. */
      source: ValueSource<string>;
      fit?: 'fill' | 'contain';
      fallback?: 'none' | 'placeholder';
    };
  };
  qrCode: {
    qrCode: {
      source: ValueSource<string>;
      color?: ValueSource<string>;
      backgroundColor?: ValueSource<string>;
      errorCorrection?: import('../../types/component').QrCodeErrorCorrection;
      fit?: 'contain' | 'cover' | 'fill';
    };
  };
  rating: {
    rating: {
      source: ValueSource<number>;
      maxRating?: ValueSource<number>;
      step?: number;
      editable?: boolean;
      glyph?: RatingGlyphId;
    };
  };
  color: {
    color: {
      source: ValueSource<string>;
      editable?: boolean;
      format?: 'hex' | 'rgba';
    };
  };
  button: {
    button: {
      label: ValueSource<string>;
      /** Optional outbound href (URL / mailto / tel / item:…). */
      href?: ValueSource<string>;
      icon?: AssetRef;
      variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
      action: ComponentPressHandler;
      confirm?: ConfirmDialog;
    };
  };
  icon: {
    icon: {
      name: ValueSource<string>;
      action?: ComponentPressHandler;
      confirm?: ConfirmDialog;
    };
  };
  group: {
    group?: {
      clipContent?: boolean;
    };
  };
  spacer: {
    spacer?: {
      flex?: number;
    };
  };
  dragHandle: {
    dragHandle?: Record<string, never>;
  };
  line: {
    line: {
      orientation?: 'horizontal' | 'vertical';
      strokeWidth?: ValueSource<number>;
      color?: ValueSource<string>;
    };
  };
  slider: {
    slider: {
      source: ValueSource<number>;
      min?: ValueSource<number>;
      max?: ValueSource<number>;
      step?: number;
      valueType?: 'integer' | 'float';
      orientation?: 'horizontal' | 'vertical';
      editable?: boolean;
    };
  };
  timerDisplay: {
    timerDisplay: {
      timerId?: ValueSource<string>;
      maxUnit?: 'day' | 'hour' | 'minute' | 'second';
    };
  };
  table: {
    table: {
      /** DEC-077 list/matrix bind (localState / itemVariable list). */
      source?: ValueSource<unknown>;
      tableSource?: ValueSource<string>;
      editable?: boolean;
      schema?: import('../../types/category').TablePropertySchema;
      visibleRowCount?: number;
    };
  };
  chart: {
    chart: {
      chartType: import('../../types/binding').ValueSource<
        import('../../types/component').LayoutChartType
      >;
      orientation?: import('../../types/component').ChartOrientation;
      binCount?: ValueSource<number>;
      /** DEC-078 list/matrix bind (localState / itemVariable list). */
      source?: ValueSource<unknown>;
      tableSource?: ValueSource<string>;
      schema?: import('../../types/category').TablePropertySchema;
      xColumn?: string;
      /** Named Y series (preferred). Legacy yColumn/ySource migrate to series[0]. */
      series?: import('../../types/component').ChartSeriesDef[];
      yColumn?: string;
      colorColumn?: string;
      labelColumn?: string;
      xSource?: ValueSource<unknown>;
      ySource?: ValueSource<unknown>;
      title?: ValueSource<string>;
      xAxisLabel?: ValueSource<string>;
      yAxisLabel?: ValueSource<string>;
      ySecondaryAxisLabel?: ValueSource<string>;
      xMin?: ValueSource<number>;
      xMax?: ValueSource<number>;
      yMin?: ValueSource<number>;
      yMax?: ValueSource<number>;
      strokeWidth?: ValueSource<number>;
      seriesColor?: ValueSource<string>;
      axisColor?: ValueSource<string>;
    };
  };
  listView: {
    listView: {
      source: ValueSource<unknown>;
      selectionSource: ValueSource<number>;
      editable?: boolean;
    };
  };
}

// ── Type-narrowing helper ─────────────────────────────────────────────────────

/**
 * Narrows CompiledRuntimeNode.props to the typed shape for the given M3 type.
 *
 * Trust boundary: the v2 validator guarantees structural correctness before
 * rendering. Renderers call this once at the top of render() to get typed
 * access rather than casting individually on each prop access (DEC-020).
 */
export function extractTypedProps<T extends M3RenderableType>(
  _type: T,
  props: Record<string, unknown>,
): CompiledPropsByType[T] {
  return props as unknown as CompiledPropsByType[T];
}

// ── Recursive child dispatch ──────────────────────────────────────────────────

/**
 * Dispatches a child node through the renderer registry.
 * Group renderers use this to recursively render each entry in
 * CompiledRuntimeNode.childIds. Returns null for an unknown nodeId.
 */
export type ChildRenderFunction = (childNodeId: string) => RenderDescriptor | null;

// ── Renderer context ──────────────────────────────────────────────────────────

/**
 * Execution context passed to ComponentRenderer.render().
 *
 * expressionEngine is null when no engine has been wired (default for M3
 * pipeline callers). Renderers must fail-open for any expression-driven
 * value when engine is null: treat as visible=true, enabled=true, and
 * omit the affected prop (DEC-019).
 */
export interface ComponentRenderContext<TProps> {
  node: CompiledRuntimeNode;
  resolvedProps: TProps;
  resolvedBindingValue?: unknown;
  visible: boolean;
  enabled: boolean;
  childRenderer: ChildRenderFunction;
  expressionEngine: ExpressionEngine | null;
  /** Binding context for the current item; required for expression evaluation. */
  bindingContext?: BindingContext;
  issues: ValidationIssue[];
  /** Compiled template for category/action lookups (enum, button renderers). */
  compiledTemplate: CompiledRuntimeTemplate;
  /** Optional asset resolution context for image renderers. */
  assetContext?: AssetResolutionContext;
  /** Active UI locale for LocalizedText display channels. */
  locale?: string;
}

// ── Renderer contract ─────────────────────────────────────────────────────────

/**
 * A renderer for a single M3 component type.
 *
 * Never throws. Append ValidationIssues to context.issues for all error paths.
 */
export interface ComponentRenderer<TProps> {
  readonly supportedType: M3RenderableType;
  render(context: ComponentRenderContext<TProps>): RenderDescriptor;
}

// ── Registry dispatch input ───────────────────────────────────────────────────

/**
 * Assembled input to RendererRegistry.dispatch().
 * Bridges previewPipeline or RenderService output → renderer.
 *
 * visible and enabled should default to true when the governing rule is
 * expression-driven and expressionEngine is null (DEC-019 fail-open).
 */
export interface NodeRenderInput {
  node: CompiledRuntimeNode;
  resolvedBindingValue?: unknown;
  visible: boolean;
  enabled: boolean;
  issues: ValidationIssue[];
  /** Compiled template passed through to renderer context for enum/button lookups. */
  compiledTemplate: CompiledRuntimeTemplate;
  /** Optional asset context for image policy. */
  assetContext?: AssetResolutionContext;
  /** Optional expression engine for evaluating visibility/enabled expressions (M6+). */
  expressionEngine?: ExpressionEngine | null;
  /** Binding context for the current item; required for expression evaluation. */
  bindingContext?: BindingContext;
  /** Active UI locale for LocalizedText display channels. */
  locale?: string;
}
